import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCustomerInfo } from 'redux/actions'
import Button from 'components/Button';
import SubHeader from 'components/SubHeader';
import StatusBar from 'components/StatusBar';
import StatusTooltip from 'components/StatusTooltip';
import FooterBar from 'components/FooterBar';
import Spinner from 'components/Spinner';
import ModalAddRoom from 'screens/ModalAddRoom';
import ModalGroupCancel from 'screens/ModalGroupCancel';
import ModalCancelConfirm from 'screens/ModalCancelConfirm';
import ModalCheckOut from 'screens/ModalCheckOut';
import ModalCheckOutConfirm from 'screens/ModalCheckOutConfirm';
import ModalSettlePayment from 'screens/ModalSettlePayment';
import CreditCards from 'components/BookingMain/CreditCards';
import AddNewCreditCard from 'components/BookingMain/AddNewCreditCard';
import BookingInformation from './BookingInformation';
import GuestInformation from 'components/BookingMain/GuestInformation';
import ReviewCheckIn from 'components/BookingMain/ReviewCheckIn';
import GeneralGroupNotes from './GeneralGroupNotes';
import MetaInformation from 'components/BookingMain/MetaInformation';
// import ActivityHistory from 'components/BookingMain/ActivityHistory';
import BillOfSale from 'components/BookingMain/BillOfSale';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_GROUP_DETAILS_BY_GROUP_ID,
  UPDATE_GROUP_BY_GROUP_ID
} from 'graphql/service';
import {
  Content,
  ContentColumn,
  Container,
} from 'components/BookingMain/styles';
import { debouncedErrorMessage } from 'utils/messageUtil';
import moment from 'moment-timezone';
import { TIME_ZONE } from 'constants/constant';
import {
  DEFAULT_CHECK_IN_HOUR,
  DEFAULT_CHECK_IN_MINUTE,
  DEFAULT_CUT_OFF_HOUR,
  DEFAULT_CUT_OFF_MINUTE
} from 'constants/constant';
import { message } from 'antd';

const GroupDetails = () => {

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const customerInfo = useSelector(
    (state) => state.customerInfoReducer.customerInfo
  );

  const groupId = useParams().groupId

  const [groupStatus, setGroupStatus] = useState('')

  //ModalGroupCancel
  const [cancelConfirmIsOpen, setCancelConfirmIsOpen] = useState(false)
  const [cancelRoomParams, setCancelRoomParams] = useState({})

  //ModalCheckOut
  const [checkOutConfirmIsOpen, setCheckOutConfirmIsOpen] = useState(false)
  const [checkOutBookings, setCheckOutBookings] = useState([])

  //GuestInformation
  const [customerInfoError, setCustomerInfoError] = useState({})
  const [updateGroupByGroupId, { loading: updateGroupLoading }] = useMutation(UPDATE_GROUP_BY_GROUP_ID, {
    onCompleted: (data) => {
      setBill(data.updateGroup.bills[0] || {})
      message.success('Update successfully')
    },
    onError: (error) => {
      debouncedErrorMessage(error.message)
    }
  })

  const handleSaveChanges = () => {
    if (updateGroupLoading) return
    updateGroupByGroupId({
      variables: {
        groupId,
        input: {
          fullName: customerInfo.fullName?? null,
          organization: customerInfo.organization?? null,
          email: customerInfo.email?? null,
          phoneNumber: customerInfo.phoneNumber?? null,
          streetAddress: customerInfo.streetAddress?? null,
          streetAddressLine2: customerInfo.streetAddressLine2?? null,
          city: customerInfo.city?? null,
          province: customerInfo.province?? null,
          postalCode: customerInfo.postalCode?? null,
          country: customerInfo.country?? null,
          travelDocumentType: customerInfo.travelDocumentType?? null,
          travelDocumentNumber: customerInfo.travelDocumentNumber?? null,
          isFirstNations: customerInfo.isFirstNations,
          subscribedToMarketingEmails: customerInfo.subscribedToMarketingEmails,
          subscribedToMarketingTexts: customerInfo.subscribedToMarketingTexts
        }
      }
    })
  }

  //CreditCards
  const [paymentRecords, setPaymentRecords] = useState([])

  //BillOfSale
  const [bill, setBill] = useState({})

  //GeneralGroupNotes
  const [groupNotes, setGroupNotes] = useState([])

  const { data: groupData, loading, error, refetch } = useQuery(GET_GROUP_DETAILS_BY_GROUP_ID, {
    variables: { groupId: groupId },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  })

  const groupByID = groupData?.groupByID;
  const groupName = groupByID?.name ?? '';
  const bookings = groupByID?.bookings ?? [];
  const allSame = bookings.length > 0 ? bookings.every(b => b.status === bookings[0].status) : true;
  const mixedGroup = !allSame;
  const primaryTenantName = bookings[0]?.fullName ?? '';
  const scan = groupByID?.identityScans?.[0] || {};

  useEffect(() => {
    if (!groupByID) return;

    setGroupStatus(groupByID?.status ?? '')
    setGroupNotes(groupByID?.notes ?? [])
    setPaymentRecords(groupByID?.paymentCards ?? [])
    setBill(groupByID?.bills?.[0] || {})

    const tenant = groupByID?.tenants?.[0]?.tenant;
    const user = tenant?.user;

    const customerInfo = {
      userId: user?.userId ?? '',
      tenantId: tenant?.tenantId ?? '',
      fullName: groupByID.fullName ?? '',
      organization: groupByID.organization ?? '',
      email: groupByID.email ?? '',
      phoneNumber: groupByID.phoneNumber ?? '',
      streetAddress: groupByID.streetAddress ?? '',
      streetAddressLine2: groupByID.streetAddressLine2 ?? '',
      city: groupByID.city ?? '',
      province: groupByID.province ?? '',
      postalCode: groupByID.postalCode ?? '',
      country: groupByID.country ?? '',
      dateOfBirth: user?.dateOfBirth ?? '',
      isFirstNations: groupByID.isFirstNations ?? false,
      subscribedToMarketingEmails: groupByID.subscribedToMarketingEmails ?? false,
      subscribedToMarketingTexts: groupByID.subscribedToMarketingTexts ?? false,
    };

    dispatch(setCustomerInfo(customerInfo));
  }, [groupByID, dispatch]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  const earlyCheckIn = bookings.find(booking => (moment().tz(TIME_ZONE) < moment(booking.checkInDate).tz(TIME_ZONE).set({ hour: DEFAULT_CHECK_IN_HOUR, minute: DEFAULT_CHECK_IN_MINUTE, second: 0 })) && (moment(booking.checkInDate).tz(TIME_ZONE).set({ hour: DEFAULT_CUT_OFF_HOUR, minute: DEFAULT_CUT_OFF_MINUTE, second: 0 }) <= moment().tz(TIME_ZONE)))

  const checkedInBookings = bookings.filter(booking => booking.status === 'CHECKED_IN')

  if (loading) return <Spinner />;

  const bookingInformationArray = []
  const sortedBookings = [...bookings].sort((a, b) => a.bookingId - b.bookingId)
  sortedBookings.forEach((booking)=> {
    bookingInformationArray.push(      
      <Container
        key={booking.bookingId}
      >
        <BookingInformation
          groupId={groupId}
          booking={booking}
          refetch={refetch}
        />
      </Container>      
    )
  })

  return (
    <>
      <Helmet>
        <title>Group Details - Rook Hotel PMS</title>
        <meta name="description" content="Group Details - Rook Hotel PMS" />
      </Helmet>
      <SubHeader>
        <div className="title">
          <Link
            className="highlight"
            to='/bookings'
          >
            Bookings
          </Link>
          {` > ${groupName}`}
          <StatusBar status={groupStatus} />
          {groupStatus === "CANCELLED" ? (
            <StatusTooltip
              createdAt={groupData?.groupByID?.cancelledAt}
              createdBy={groupData?.groupByID?.cancelledBy?.fullName}
              type={"Cancellation"}
              reason={groupData?.groupByID?.cancellationReason}
              amount={groupData?.groupByID?.cancellationFee}
            />
          ) : null}
        </div>
        <div className="buttonContainer">
          {(groupStatus === "PENDING" || groupStatus === "CHECKED_IN") ?(
            <ModalAddRoom
              groupId={groupId}
              refetch={refetch}
              buttonStyle={`
                width: 96px;
              `}
            />) : null
          }
          {((groupStatus === "PENDING" && bookings.length > 1) && !mixedGroup)? (
            <Button
              mainPage
              onClick={(event) => {
                if (earlyCheckIn) {
                  if (event.metaKey || event.ctrlKey) {
                    // Open new tab with absolute URL
                    window.open(`https://pms.rookhotel.com/group-check-in/early-check-in/${groupId}`, '_blank', 'noopener,noreferrer');
                  } else {
                    // Normal navigation
                    navigate(`/group-check-in/early-check-in/${groupId}`);
                  }
                } else {
                  if (event.metaKey || event.ctrlKey) {
                    // Open new tab with absolute URL
                    window.open(`https://pms.rookhotel.com/group-check-in/confirm-identity/${groupId}`, '_blank', 'noopener,noreferrer');
                  } else {
                    navigate(`/group-check-in/confirm-identity/${groupId}`);
                  }
                }
              }}
              additionalstyle={`
                width: 126px;
                margin-left: 8px;
              `}
            >
              Group Check In
            </Button>
          ) : null}
          {groupStatus === "CHECKED_OUT" ? (
            <ModalSettlePayment
              groupId={groupId}
              primaryCard={
                paymentRecords.find((paymentCard) => paymentCard.isPrimary) ||
                {}
              }
              balance={bill?.balance}
              amountToPay={bill?.amountToPay}
              setBill={setBill}
              setStatus={setGroupStatus}
              buttonStyle={`
                  width: 204px;
                  margin-left: 8px;
                `}
            />
          ) : null}
          {((groupStatus === "PENDING" || groupStatus === "CHECKED_IN") && !mixedGroup) ? (
            <ModalGroupCancel
              className="cancelButton"
              danger
              singleBooking={bookings.length <= 1}
              checkedIn={checkedInBookings.length > 0}
              buttonLabel={bookings.length > 1 ? "Cancel Entire Group": "Cancel Booking"}
              buttonStyle={bookings.length > 1 ?`
                  width: 144px;
                  margin-left: 8px;
                `: `
                  width: 126px;
                  margin-left: 8px;
                `}
              setCancelRoomParams={setCancelRoomParams}
              setConfirmIsOpen={setCancelConfirmIsOpen}
            />
          ) : null}
          {((checkedInBookings.length > 1 && groupStatus === "CHECKED_IN") && !mixedGroup) ? (
            <ModalCheckOut
              buttonStyle={`
                width: 138px;
                margin-left: 8px;
              `}
              checkedInBookings={checkedInBookings}
              checkOutBookings={checkOutBookings}
              setCheckOutBookings={setCheckOutBookings}
              setCheckOutConfirmIsOpen={setCheckOutConfirmIsOpen}
            />
          ) : null}
          {((checkedInBookings.length === 1 && groupStatus === "CHECKED_IN") && !mixedGroup) ? (
            <ModalCheckOutConfirm
              buttonLabel={bookings.length > 1 ? "Group Check Out": "Check Out"}
              buttonStyle={bookings.length > 1 ?`
                  width: 138px;
                  margin-left: 8px;
                `: `
                  width: 92px;
                  margin-left: 8px;
                `}
              isOpen={checkOutConfirmIsOpen}
              setIsOpen={setCheckOutConfirmIsOpen}
              checkOutBookings={checkedInBookings}
              refetch={refetch}
            />
          ) : null}
        </div>
      </SubHeader>
      <ModalCancelConfirm
        isOpen={cancelConfirmIsOpen}
        setIsOpen={setCancelConfirmIsOpen}
        cancelRoomParams={cancelRoomParams}
        setCancelRoomParams={setCancelRoomParams}
        fullName={primaryTenantName}
        groupId={groupId}
        refetch={refetch}
      />
      {checkedInBookings.length > 1 ? (
        <ModalCheckOutConfirm
          isOpen={checkOutConfirmIsOpen}
          setIsOpen={setCheckOutConfirmIsOpen}
          checkOutBookings={checkOutBookings}
          refetch={refetch}
        />
      ) : null}
      <Content
        additionalstyle={`
          margin-top: 100px;
        `}
      >
        <ContentColumn>
          <Container>
            <GuestInformation
              type={'BOOKING'}
              customerStatus={'PRIMARY'}
              customerInfoError={customerInfoError}
              setCustomerInfoError={setCustomerInfoError}
              btnInfo={
                <Button
                  mainPage
                  success
                  onClick={handleSaveChanges}
                  data-loading={updateGroupLoading}
                  additionalstyle={`
                    width: 113px;
                    margin-top: 24px;
                  `}
                >
                  {updateGroupLoading? null: 'Save Changes'}
                </Button>
              }
            />
          </Container>
          <Container>
            <ReviewCheckIn scan={scan}/>
          </Container>
          <Container>
            <BillOfSale
              bill={bill}
              setBill={setBill}
              bookings={bookings}
              primaryCard={
                paymentRecords.find((paymentCard) => paymentCard.isPrimary) ||
                {}
              }
              groupId={groupId}
              groupStatus={groupStatus}
            />
          </Container>
          <Container>
            <CreditCards
              paymentRecords={paymentRecords}
              balance={bill?.balance}
              setPaymentRecords={setPaymentRecords}
            />
            <AddNewCreditCard groupId={groupId} setPaymentRecords={setPaymentRecords}/>
          </Container>
        </ContentColumn>
        <ContentColumn>
          {bookingInformationArray}
          <Container>
            <GeneralGroupNotes
              notes={groupNotes}
              setNotes={setGroupNotes}
              groupId={groupId}
              headerString={"GENERAL GROUP NOTES"}
            />
          </Container>
          <Container>
            <MetaInformation
              created={groupData?.groupByID.createdAt}
              origin={groupData?.groupByID.origin}
              confirmationNumber={groupData?.groupByID.primaryBookingCode}
              channel={groupData?.groupByID.channel}
              campaignTag={"Christmas Promo 2020"}
              otaBookingNumber={groupData?.groupByID.otaBookingNumber}
            />
          </Container>
          <Container>
            {/* FIXME: should be group id
            <ActivityHistory
              bookingId={bookings[0]?.bookingId || ""}
            /> */}
          </Container>
        </ContentColumn>
      </Content>
      <FooterBar />
    </>
  );
}

export default GroupDetails;